// The institution specialisation of the generic reference value
// (INSTITUTION-TYPEAHEAD-1, D-202.3).
//
// A matched institution stores `{ display, ref }` where `ref` is the
// `institution_registry.key`. An institution the planner typed and did not
// match stores `{ display }` with no `ref` — which is a complete and correct
// value, not a degraded one (the s201 ruling: free text always wins).
//
// Pure — no React, no DOM. Everything here delegates to
// `shared/reference-value.ts`; this file adds naming and the registry-key
// meaning, never a second parse.
//
// Out of scope by ruling: resolving `ref` to the institution's contact pack.
// That is the REASON the key is stored, and it is a separate build.

import {
  formatReferenceSummary,
  parseReference,
  toReferenceValue,
  type ReferenceValue,
} from "./reference-value";

/** A stored institution value. `ref`, when present, is an `institution_registry.key`. */
export type InstitutionRef = ReferenceValue;

/** Parse a stored institution value, or null when it is not a reference object
 *  (a plain string typed before the fieldType flip, or empty). */
export function parseInstitution(value: string | null | undefined): InstitutionRef | null {
  return parseReference(value);
}

/** The institution name to DISPLAY. A plain string passes through unchanged, so a
 *  legacy value renders exactly as it always did (G8). */
export function formatInstitutionSummary(value: string | null | undefined): string {
  return formatReferenceSummary(value);
}

/** Build the value to store. `key` is the registry key when the planner picked a
 *  suggestion, and omitted when they typed their own. */
export function toInstitutionValue(name: string, key?: string): string {
  return toReferenceValue(name, key);
}
